import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { mdiFileRemove } from '@mdi/js';
import { useMutation, useQuery } from '@apollo/client/react';

import { BoxLoader } from 'common/components/Loader';
import { useCertificateList } from 'certificate/hooks';
import {
    TableCell,
    TableHead,
    TableActionBar,
    useSelection,
} from 'common/components/Table';
import { FilterBar } from 'common/components/FilterBar';
import { Pagination } from 'common/components/Pagination';
import { CertificatesManagementTableRow } from 'certificate/components/CertificatesManagementTable/CertificatesManagementTableRow';
import {
    CurrentUserDocument,
    DeleteCertificatesDocument,
    IRole,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { Checkbox } from 'common/components/Checkbox';
import { Tooltip } from 'common/components/Tooltip';
import { IconButton } from 'common/components/IconButton';

interface IProps {
    onLoading?(loading: boolean): void;
}

export const CertificatesManagementTable = ({ onLoading }: IProps) => {
    const [translate] = useTranslation();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const { showApolloError } = useApolloError();
    const [displaySnackbar] = useSnackbar();
    const [deleteCertificates, { loading: loadingDeleteCertificates }] =
        useMutation(DeleteCertificatesDocument);

    const { data: currentUserData, loading: loadingCurrentUser } =
        useQuery(CurrentUserDocument);
    const currentUser = currentUserData?.currentUser;
    const currentUserIsManager = currentUser?.roles?.includes(IRole.Manager);

    const {
        certificates,
        filters,
        filterValues,
        loading: certificatesLoading,
        paginationSettings,
        setPage,
        onSearch,
        onFilterChange,
    } = useCertificateList();

    const loading =
        certificatesLoading || loadingDeleteCertificates || loadingCurrentUser;

    const { ...selectionProps } = useSelection(certificates);
    const { isSelected, rows, selected, onSelectAll } = selectionProps;

    const handleSearch = (searchValue: string) => {
        setPage(1);
        onSearch?.(searchValue);

        if (!!selected?.length) onSelectAll?.();
    };

    const resetSettings = () => {
        if (!!selected?.length) onSelectAll?.();

        setPage(1);
        onSearch('');
    };

    const handleDelete = async () => {
        try {
            await deleteCertificates({
                variables: { ids: selected },
                update: (cache) => {
                    // Remove all deleted certificates from cache to make sure they are gone throughout the system
                    selected.forEach((certificateId) => {
                        cache.evict({ id: `Certificate:${certificateId}` });
                    });

                    cache.evict({ fieldName: 'certificates' });

                    cache.gc();
                },
            });

            displaySnackbar(
                translate(
                    'certificatesManagementPage.actionSuccess.bulkDelete',
                    {
                        count: selected.length,
                    }
                ),
                {
                    autoHideDuration: 30000,
                    variant: 'success',
                }
            );
        } catch (error) {
            showApolloError(error);

            return;
        }

        setShowDeleteDialog(false);
        resetSettings();
    };

    useEffect(() => {
        onLoading?.(loading);
    }, [loading, onLoading]);

    const tableActions = currentUserIsManager ? (
        <Tooltip
            title={translate('certificatesManagementPage.bulkDeleteTooltip')}
        >
            <Box>
                <IconButton
                    disabled={!isSelected}
                    iconPath={mdiFileRemove}
                    iconSize="2.4rem"
                    onClick={() => setShowDeleteDialog(true)}
                />
            </Box>
        </Tooltip>
    ) : null;

    return (
        <>
            <FilterBar
                useFilterDrawer
                disabled={loading}
                filterDrawerProps={{
                    title: translate('filterForm.certificates.title'),
                    description: translate(
                        'filterForm.certificates.description'
                    ),
                }}
                formFilters={filters}
                formFilterValues={filterValues}
                initialSearchValue={filterValues.q}
                placeholder={translate('filterBarPlaceholder')}
                onFiltersChange={(values) => {
                    setPage(1);

                    onFilterChange?.(values);

                    if (!!selected?.length) onSelectAll?.();
                }}
                onSearch={handleSearch}
                onSearchClear={() => handleSearch('')}
            />

            {!loading && !!rows.length && (
                <TableContainer>
                    {currentUserIsManager && (
                        <TableActionBar
                            actions={tableActions}
                            selectionProps={selectionProps}
                        />
                    )}
                    <Table>
                        <TableHead>
                            <TableRow>
                                {currentUserIsManager && (
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={false}
                                            disabled={isSelected}
                                            onChange={onSelectAll}
                                        />
                                    </TableCell>
                                )}
                                <TableCell colSpan={2}>
                                    {translate(
                                        'certificatesManagementPage.name'
                                    )}
                                </TableCell>

                                <TableCell colSpan={2}>
                                    {translate(
                                        'certificatesManagementPage.validity'
                                    )}
                                </TableCell>

                                <TableCell colSpan={2}>
                                    {translate(
                                        'certificatesManagementPage.scoringPoints'
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {rows?.map(({ index, onSelect, isSelected }) => {
                                const certificate = certificates[index] || null;

                                if (!certificate) return null;

                                return (
                                    <CertificatesManagementTableRow
                                        certificate={certificate}
                                        isEditable={currentUserIsManager}
                                        isSelected={isSelected}
                                        key={certificate.id}
                                        onSelect={onSelect}
                                    />
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {loading && <BoxLoader />}

            {!loading && !rows.length && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography>
                        {translate('noOptionsText.certificates')}
                    </Typography>
                </Box>
            )}

            {!loading && (
                <Box sx={{ mt: 4 }}>
                    <Pagination
                        page={paginationSettings.page}
                        pageAmount={paginationSettings.pageAmount}
                        totalsAmount={paginationSettings.count}
                        totalsText={translate(
                            'certificatesManagementPage.certificates',
                            {
                                count: paginationSettings.count,
                            }
                        )}
                        onChange={(page: number) => {
                            setPage(page);
                        }}
                    />
                </Box>
            )}

            <ConfirmDialog
                confirmText={translate('delete')}
                loading={loadingDeleteCertificates}
                open={showDeleteDialog}
                title={translate(
                    'certificatesManagementPage.certificatesDeleteDialog.title',
                    {
                        count: selected.length,
                    }
                )}
                onCancel={() => {
                    setShowDeleteDialog(false);
                }}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={handleDelete}
            >
                {translate(
                    'certificatesManagementPage.certificatesDeleteDialog.text',
                    {
                        count: selected.length,
                    }
                )}
            </ConfirmDialog>
        </>
    );
};
